/**
 * The Labeler Label node's logic that does not need React, tldraw or the network: the
 * API shapes, the manifest compile and its inverse, value options, locale resolution,
 * and the publish gate's reason.
 *
 * No imports at all, on purpose: `scripts/verify-labeler-label.mjs` loads this file
 * under bare `node --experimental-strip-types`, the way the time-window schedule is
 * checked.
 *
 * The contract is SPEC-atproto-labels-contract-2026-10.md.
 */

// ---------------------------------------------------------------------------
// API shapes: feed-processor `app/api/labelers.py`; `status` is membership-service's
// `LabelerStatus`, passed through.

type LabelerState = "unknown" | "backfilling" | "ready" | "unreachable" | "error";

interface LabelerStatus {
  did: string;
  endpoint: string | null;
  state: LabelerState;
  cursor: number;
  head_seq: number | null;
  /** 0..1 */
  progress: number;
  account_labels: number;
  post_labels: number;
  last_event_at: string | null;
  /**
   * The latest failure. On a `ready` labeler this means it is currently failing but its
   * label sets are still served (stale, never emptied).
   */
  error: string | null;
  // The 2026-10-21 addendum's fields. Optional: older membership-service builds don't
  // send them.
  /** ms timestamp of the last frame or successful connect, or null. */
  last_ok_at?: number | null;
  consecutive_failures?: number;
  /** Tracked by discovery (enough likes), not because someone asked for it. */
  auto_tracked?: boolean;
}

export interface LabelerSummary {
  did: string;
  handle: string;
  displayName?: string | null;
  avatar?: string | null;
  likeCount?: number | null;
  description?: string | null;
}

interface LabelValueDefinitionStrings {
  lang: string;
  name: string;
  description: string;
}

/** `com.atproto.label.defs#labelValueDefinition`, as `getServices?detailed=true` returns it. */
interface LabelValueDefinition {
  identifier: string;
  severity: "inform" | "alert" | "none" | (string & {});
  blurs: "content" | "media" | "none" | (string & {});
  defaultSetting?: string;
  adultOnly?: boolean;
  locales: LabelValueDefinitionStrings[];
}

export interface LabelerDetail extends LabelerSummary {
  labelValues: string[];
  labelValueDefinitions: LabelValueDefinition[];
  status: LabelerStatus;
}

// ---------------------------------------------------------------------------
// The manifest conditions.

/** Mirrors the engines' validation (rust-smasher `validation.rs`, feed-processor save). */
export const LABEL_VALUE_PATTERN = /^!?[a-z0-9-]{1,128}$/;
export const LABELER_DID_PATTERN =
  /^did:(plc:[a-z2-7]{24}|web:[A-Za-z0-9.:%-]+)$/;
export const MAX_LABEL_VALUES = 50;

type Operator = "in" | "not_in";
type Subject = "account" | "post";

interface LabelerLabelFields {
  labelerDid: string;
  values: string[];
  subject: Subject;
  operator: Operator;
  didField?: string;
}

type AccountLabelArgs =
  | [string, string[], Operator]
  | [string, string[], Operator, string];
type PostLabelArgs = [string, string[], "not_in"];

/**
 * The node as a manifest leaf, without its metadata.
 *
 * `post_label` has no include form -- a post is labelled after we have matched it, so
 * inclusion by post label would match nothing -- so its operator is forced, not read.
 */
export function compileLabelerLabel(
  fields: LabelerLabelFields,
): { account_label: AccountLabelArgs } | { post_label: PostLabelArgs } {
  if (fields.subject === "post") {
    return { post_label: [fields.labelerDid, [...fields.values], "not_in"] };
  }

  return {
    account_label: fields.didField
      ? [fields.labelerDid, [...fields.values], fields.operator, fields.didField]
      : [fields.labelerDid, [...fields.values], fields.operator],
  };
}

/** The inverse of `compileLabelerLabel`, or undefined for any other leaf. */
export function readLabelerLabel(
  filter: Record<string, unknown>,
): LabelerLabelFields | undefined {
  const values = (raw: unknown) =>
    Array.isArray(raw) ? raw.filter((v): v is string => typeof v === "string") : [];

  if (Array.isArray(filter.account_label)) {
    const [labelerDid, vals, operator, didField] = filter.account_label as unknown[];
    return {
      labelerDid: typeof labelerDid === "string" ? labelerDid : "",
      values: values(vals),
      subject: "account",
      operator: operator === "in" ? "in" : "not_in",
      // Only when present: an absent optional prop and `undefined` are not the same
      // record to tldraw's validator.
      ...(typeof didField === "string" && didField ? { didField } : {}),
    };
  }

  if (Array.isArray(filter.post_label)) {
    const [labelerDid, vals] = filter.post_label as unknown[];
    return {
      labelerDid: typeof labelerDid === "string" ? labelerDid : "",
      values: values(vals),
      subject: "post",
      operator: "not_in",
    };
  }

  return undefined;
}

/** The labeler DIDs every `account_label` / `post_label` in a filter tree refers to. */
export function labelerDidsInFilter(filter: unknown): string[] {
  const found = new Set<string>();

  const walk = (node: unknown) => {
    if (!node || typeof node !== "object") return;
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    const record = node as Record<string, unknown>;
    const leaf = readLabelerLabel(record);
    if (leaf?.labelerDid) found.add(leaf.labelerDid);
    walk(record.and);
    walk(record.or);
    // `each: [var, body]` -- the variable is a string and walks to nothing.
    walk(record.each);
  };

  walk(filter);
  return [...found];
}

// ---------------------------------------------------------------------------
// The picker.

/**
 * Turn whatever was pasted into the labeler box into something the detail endpoint
 * takes: a DID or a handle. People paste profile URLs and `@handle`s as often as bare
 * identifiers, and both are unambiguous.
 */
export function normalizeLabelerInput(raw: string): string {
  let value = raw.trim();
  const profile = value.match(/^https?:\/\/[^/]+\/profile\/([^/?#]+)/);
  if (profile?.[1]) value = decodeURIComponent(profile[1]);
  if (value.startsWith("@")) value = value.slice(1);
  return value;
}

/** Whether `value` is worth asking the server about: a DID, or something handle-shaped. */
export function isLookupableLabeler(value: string): boolean {
  if (LABELER_DID_PATTERN.test(value)) return true;
  // A handle is a domain: at least one dot, no spaces. Anything less is a half-typed
  // word, and asking about it would only produce a 404 per keystroke.
  return /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/.test(value);
}

/**
 * Pick the strings for a label value in the reader's language.
 *
 * `@atproto/api` interprets a definition's behaviour (`interpretLabelValueDefinition`)
 * but leaves choosing a locale to the client -- the Bluesky app has its own helper for
 * it. Same order here: an exact tag, then the bare language, then English, then
 * whatever the labeler wrote first. 36% of published definitions have no description,
 * so an empty one falls back to the identifier rather than to nothing.
 */
export function resolveLabelStrings(
  definition: Pick<LabelValueDefinition, "identifier" | "locales">,
  languages: readonly string[] = typeof navigator === "undefined"
    ? ["en"]
    : navigator.languages,
): { name: string; description: string } {
  const locales = definition.locales ?? [];
  const base = (tag: string) => tag.toLowerCase().split("-")[0];
  let chosen: LabelValueDefinitionStrings | undefined;

  for (const language of [...languages, "en"]) {
    chosen =
      locales.find((l) => l.lang.toLowerCase() === language.toLowerCase()) ??
      locales.find((l) => base(l.lang) === base(language));
    if (chosen) break;
  }
  chosen ??= locales[0];

  return {
    name: chosen?.name?.trim() || definition.identifier,
    description: chosen?.description?.trim() || definition.identifier,
  };
}

export interface LabelValueOption {
  value: string;
  name: string;
  description: string;
  severity?: string;
  blurs?: string;
  adultOnly?: boolean;
  /**
   * `defined`: declared with a definition. `undeclared`: listed in `labelValues` with no
   * definition. `other`: typed in by the author; the labeler says nothing about it.
   */
  source: "defined" | "undeclared" | "other";
}

/**
 * Every value the picker offers, plus every value the node already holds.
 *
 * Labelers emit values they never declare -- Skywatch's `twitter-x` is 252k labels with
 * no definition -- so `labelValues` without a definition are offered too, and anything
 * the node holds that neither list knows (an "other value…" entry) is kept, so it can
 * be seen and removed.
 */
export function labelValueOptions(
  labeler: Pick<LabelerDetail, "labelValues" | "labelValueDefinitions"> | undefined,
  selected: readonly string[],
  languages?: readonly string[],
): LabelValueOption[] {
  const options: LabelValueOption[] = [];
  const seen = new Set<string>();

  for (const definition of labeler?.labelValueDefinitions ?? []) {
    if (seen.has(definition.identifier)) continue;
    seen.add(definition.identifier);
    options.push({
      value: definition.identifier,
      ...resolveLabelStrings(definition, languages),
      severity: definition.severity,
      blurs: definition.blurs,
      adultOnly: Boolean(definition.adultOnly),
      source: "defined",
    });
  }

  for (const value of [...(labeler?.labelValues ?? []), ...selected]) {
    if (seen.has(value)) continue;
    seen.add(value);
    options.push({
      value,
      name: value,
      description: value,
      source: labeler?.labelValues?.includes(value) ? "undeclared" : "other",
    });
  }

  return options;
}

export function labelerName(
  labeler: Pick<LabelerSummary, "displayName" | "handle"> | undefined,
  fallback?: string,
) {
  return labeler?.displayName?.trim() || labeler?.handle || fallback || "this labeler";
}

/**
 * A `backfilling` labeler that has never got anywhere and is failing: no head seq, no
 * progress, an error. Before membership-service had an `unreachable` state this is what
 * a dead labeler looked like (media-ownership.aendra.dev: endpoint 502, "Syncing… 0%"
 * forever), so it is shown as one -- still blocking, but honest about why.
 */
export function looksUnreachable(status: LabelerStatus | undefined): boolean {
  return (
    status?.state === "backfilling" &&
    (status.progress ?? 0) === 0 &&
    status.error != null &&
    status.head_seq == null
  );
}

/** The spec's copy for `unreachable`, word for word. */
export function unreachableMessage(labeler: LabelerDetail) {
  return `${labelerName(labeler)}'s label server is offline (Graze can't reach it). Feeds can't use it until it comes back.`;
}

/** For a labeler that `looksUnreachable` but has not been declared so. */
export function retryingMessage(labeler: LabelerDetail) {
  return `Can't reach ${labelerName(labeler)}'s label server yet (retrying)…`;
}

/** "5 minutes ago", "yesterday", "just now". */
export function relativeTime(thenMs: number, nowMs: number = Date.now()): string {
  const seconds = Math.round((thenMs - nowMs) / 1000);
  if (Math.abs(seconds) < 45) return "just now";
  const format = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["minute", 60],
    ["hour", 60 * 60],
    ["day", 60 * 60 * 24],
  ];
  let chosen = units[0]!;
  for (const unit of units) {
    if (Math.abs(seconds) >= unit[1]) chosen = unit;
  }
  return format.format(Math.round(seconds / chosen[1]), chosen[0]);
}

/**
 * A `ready` labeler that is currently failing: its labels are still served, so this is
 * a note, not a block. Undefined when there is nothing to say.
 */
export function staleReadyNote(
  labeler: LabelerDetail,
  nowMs: number = Date.now(),
): string | undefined {
  const { state, error, last_ok_at } = labeler.status ?? {};
  if (state !== "ready" || error == null) return undefined;
  const name = labelerName(labeler);
  if (typeof last_ok_at !== "number") {
    return `${name} is currently unreachable, using the labels we have`;
  }
  return `Last synced ${relativeTime(last_ok_at, nowMs)}; ${name} is currently unreachable, using the labels we have`;
}

const LABELER_SYNC_POLL_MS = 5_000;
const LABELER_SLOW_POLL_MS = 30_000;

/**
 * How often to re-ask about a labeler, or false to stop.
 *
 * - Backfilling: 5s, so the progress bar moves.
 * - Unreachable (or looks it), error, or a failed fetch: 30s -- a dead server won't come
 *   back faster for being asked.
 * - Ready but failing: 30s, so the stale note clears when it recovers.
 * - Ready: stop. membership-service never demotes a labeler that has caught up.
 */
export function labelerPollInterval(
  status: LabelerStatus | undefined,
  fetchFailed = false,
): number | false {
  const state = status?.state;
  if (state === "ready") return status?.error != null ? LABELER_SLOW_POLL_MS : false;
  if (
    fetchFailed ||
    state === "unreachable" ||
    state === "error" ||
    looksUnreachable(status)
  ) {
    return LABELER_SLOW_POLL_MS;
  }
  return LABELER_SYNC_POLL_MS;
}

/** "Syncing Skywatch… 63%". */
export function syncingMessage(labeler: LabelerDetail) {
  const percent = Math.floor(
    Math.min(1, Math.max(0, labeler.status.progress ?? 0)) * 100,
  );
  return `Syncing ${labelerName(labeler)}… ${percent}%`;
}

// ---------------------------------------------------------------------------
// The publish gate.

/**
 * Why a feed using these labelers cannot be published yet, or undefined if it can.
 *
 * 🔴 Publishing early is not harmless. Until a labeler has replayed its history its
 * label sets are null, and the engines WITHHOLD the condition rather than read it as an
 * empty set -- an empty set would make `not_in` pass everyone, the exact failure a
 * safety exclusion exists to prevent. A feed published mid-sync goes quiet at best.
 *
 * Anything short of `ready` blocks, including a status we could not fetch: "we don't
 * know whether it's synced" is not a state to publish a safety rule in.
 */
export function labelerPublishBlockReason(
  entries: {
    did: string;
    data?: LabelerDetail;
    isError: boolean;
    isPending: boolean;
  }[],
): string | undefined {
  const reasons: string[] = [];

  for (const { did, data, isError, isPending } of entries) {
    // A malformed DID is the save gate's to report (`labelLeafProblem`).
    if (!LABELER_DID_PATTERN.test(did)) continue;
    const state = data?.status?.state;
    // Ready-but-failing publishes too: its sets are still served (see `staleReadyNote`).
    if (state === "ready") continue;
    if (data && state === "unreachable") {
      reasons.push(unreachableMessage(data));
    } else if (data && looksUnreachable(data.status)) {
      reasons.push(
        `${retryingMessage(data)} Its labels can't be used until it has synced.`,
      );
    } else if (data && state === "backfilling") {
      reasons.push(
        `${syncingMessage(data)} Its labels can't be used until it has caught up.`,
      );
    } else if (data && state === "error") {
      reasons.push(
        `${labelerName(data)} couldn't be synced${
          data.status.error ? ` (${data.status.error})` : ""
        }.`,
      );
    } else if (data) {
      reasons.push(`${labelerName(data)} hasn't started syncing yet.`);
    } else if (isError) {
      reasons.push(`Couldn't check whether ${did} has finished syncing.`);
    } else if (isPending) {
      reasons.push("Checking whether its labelers have finished syncing…");
    }
  }

  if (!reasons.length) return undefined;
  return `Can't publish yet: ${reasons.join(" ")}`;
}

// ---------------------------------------------------------------------------
// Placement.

/**
 * The message for a `post_label` anywhere but the top level of the feed. Shared by the
 * node and the save gate so the two say the same thing.
 */
export const MISPLACED_POST_LABEL_MESSAGE =
  "Post labels can only be used at the top level of a feed (not inside an OR or nested group).";

/**
 * How many `post_label` leaves sit somewhere other than a ROOT CONJUNCT -- the root leaf
 * itself, or a direct child of a root `and`.
 *
 * 🔴 Not a style note: feed-processor refuses such a manifest at save and rust-smasher at
 * load. A post label is true at match time (it is applied at serve time), so inside an
 * `or` it would make the whole `or` true and flood the feed.
 */
export function misplacedPostLabelCount(filter: unknown): number {
  let count = 0;

  const walk = (node: unknown, isRootConjunct: boolean) => {
    if (!node || typeof node !== "object" || Array.isArray(node)) return;
    const record = node as Record<string, unknown>;
    if (Array.isArray(record.post_label) && !isRootConjunct) count++;
    for (const child of Array.isArray(record.and) ? record.and : []) walk(child, false);
    for (const child of Array.isArray(record.or) ? record.or : []) walk(child, false);
    if (Array.isArray(record.each)) walk(record.each[1], false);
  };

  if (filter && typeof filter === "object" && !Array.isArray(filter)) {
    const root = filter as Record<string, unknown>;
    if (Array.isArray(root.and)) {
      for (const child of root.and) walk(child, true);
    } else {
      walk(root, true);
    }
  }

  return count;
}

/** What is wrong with one label leaf by the engines' validation rules, if anything. */
function labelLeafProblem(fields: LabelerLabelFields): string | undefined {
  if (!fields.labelerDid.trim()) return "A Labeler Label node has no labeler chosen.";
  if (!LABELER_DID_PATTERN.test(fields.labelerDid)) {
    return `"${fields.labelerDid}" is not a labeler DID — choose the labeler again.`;
  }
  if (fields.values.length === 0) return "A Labeler Label node has no labels chosen.";
  if (fields.values.length > MAX_LABEL_VALUES) {
    return `A Labeler Label node can hold at most ${MAX_LABEL_VALUES} labels.`;
  }
  const bad = fields.values.find((value) => !LABEL_VALUE_PATTERN.test(value));
  if (bad !== undefined) return `"${bad}" is not a valid label value.`;
  return undefined;
}

/**
 * Why this manifest cannot be SAVED (and so not published either), or undefined.
 *
 * Exactly the rules feed-processor applies before save (and rust-smasher at load), so
 * the author hears about them on the canvas instead of from a refused save.
 */
export function labelSaveBlockReason(filter: unknown): string | undefined {
  const problems = new Set<string>();

  const walk = (node: unknown) => {
    if (!node || typeof node !== "object" || Array.isArray(node)) return;
    const record = node as Record<string, unknown>;
    const leaf = readLabelerLabel(record);
    const problem = leaf && labelLeafProblem(leaf);
    if (problem) problems.add(problem);
    for (const child of Array.isArray(record.and) ? record.and : []) walk(child);
    for (const child of Array.isArray(record.or) ? record.or : []) walk(child);
    if (Array.isArray(record.each)) walk(record.each[1]);
  };
  walk(filter);
  if (misplacedPostLabelCount(filter) > 0) problems.add(MISPLACED_POST_LABEL_MESSAGE);

  return problems.size ? [...problems].join(" ") : undefined;
}
